import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { GridValueDisplayList } from "../components/GridValueDisplayList";
import { ValueDisplayProps } from "../components/ValueDisplay";
import { ViewJsonDialog } from "../components/ViewJsonDialog";
import { Fetcher } from "../components/Fetcher";
import { EditAdditionalSubscriptionConfig } from "../components/EditAdditionalSubscriptionConfig";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { getSubscriptionDetails } from "../services/api/subscription.api";
import {
	SubscriptionDetails,
	TwilioSubscriptionAdditionalConfiguration,
	AmazonConnectSubscriptionAdditionalConfiguration,
} from "../models/Subscription";
import { tryGetProp, mapCleType, tryJsonParse, boolToYesNo } from "../utils";
import { POW_FEATURES, hasPowFeature } from "../services/powFeatures.service";
import { store } from "../globalState/StateProvider";
import { DateDisplay } from "../components/DateDisplay";
import { WorkerListDownload } from "../components/WorkerListDownload";
import { PowFeature } from "../components/PowFeature";
import { EditAmazonConnectAdditionalSubscriptionConfig } from "../components/EditAmazonConnectAdditionalSubscriptionConfig";

interface SubscriptionDetailsPageProps {
	// subscription id to fetch details
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	wrapper: {
		marginBottom: theme.spacing(4),
	},
	invalidBadge: {
		color: theme.palette.error.dark,
		borderColor: theme.palette.error.dark,
		marginLeft: theme.spacing(2),
		textTransform: "uppercase",
	},
	title: {
		marginBottom: theme.spacing(3),
	},
}));

const renderPage = (
	data: SubscriptionDetails,
	classes: Record<any, string>,
	productId: string,
	forceUpdate: () => void,
) => {
	const isValidConfig = !!tryGetProp(false, () => data.subscription!.validSubscriptionAdditionalConfiguration);
	const cleType = tryGetProp("", () => mapCleType(data.product!.cleType));
	const isTwilioCleEnabled = cleType === "Twilio";
	const isAmazonConnectCleEnabled = cleType === "AmazonConnect";

	const tryGetTwilioConfig = () => {
		let piiPropNames = "";

		let config: TwilioSubscriptionAdditionalConfiguration = tryGetProp(undefined, () => {
			const subscriptionConfig = tryJsonParse(data.subscription!.subscriptionAdditionalConfiguration, undefined);

			piiPropNames = tryGetProp("", () =>
				Array.isArray(subscriptionConfig)
					? subscriptionConfig[0].PIIRemovalPropertyNamesCsv
					: subscriptionConfig.SubscriptionConfigurations[0].PIIRemovalPropertyNamesCsv,
			);

			return subscriptionConfig;
		});
		if (config.ApplicationInsightsName == null) {
			config = {
				SubscriptionConfigurations: tryGetProp([], () =>
					tryJsonParse(data.subscription!.subscriptionAdditionalConfiguration, []),
				),
				ApplicationInsightsName: config.ApplicationInsightsName || "",
				PIIRemovalPropertyNamesCsv: config.PIIRemovalPropertyNamesCsv,
			};
		}
		config.PIIRemovalPropertyNamesCsv = config.PIIRemovalPropertyNamesCsv || piiPropNames;
		return config;
	};

	const tryGetAmazonConnectConfig = () => {
		let piiPropNames = "";

		let config: AmazonConnectSubscriptionAdditionalConfiguration = tryGetProp(undefined, () => {
			const subscriptionConfig = tryJsonParse(data.subscription!.subscriptionAdditionalConfiguration, undefined);

			piiPropNames = tryGetProp("", () =>
				Array.isArray(subscriptionConfig)
					? subscriptionConfig[0].PIIRemovalPropertyNamesCsv
					: subscriptionConfig.SubscriptionConfigurations[0].PIIRemovalPropertyNamesCsv,
			);

			return subscriptionConfig;
		});
		if (config.ApplicationInsightsName == null) {
			config = {
				SubscriptionConfigurations: tryGetProp([], () =>
					tryJsonParse(data.subscription!.subscriptionAdditionalConfiguration, []),
				),
				ApplicationInsightsName: config.ApplicationInsightsName || "",
				PIIRemovalPropertyNamesCsv: config.PIIRemovalPropertyNamesCsv,
			};
		}
		config.PIIRemovalPropertyNamesCsv = config.PIIRemovalPropertyNamesCsv || piiPropNames;
		return config;
	};

	const detailsToDisplay: ValueDisplayProps[] = [
		{
			label: "Subscription id",
			node: tryGetProp("", () => data.subscription!.subscriptionId),
		},
		{
			label: "Product license key",
			node: tryGetProp("", () => data.subscription!.productLicenseKey),
		},
		{
			label: "Product",
			node: tryGetProp("", () => data.product!.productName),
		},
		{
			label: "Product tier",
			node: tryGetProp("", () => data.productTier!.productTierName),
		},
		{
			label: "Company",
			node: tryGetProp("", () => data.company!.companyName),
		},
		{
			label: "Company contacts",
			node: tryGetProp([<span key=""></span>], () =>
				data.contacts!.map((c) => (
					<div
						key={c.contactId}
					>{`${c.firstName} ${c.lastName} (${c.email} / +${c.phoneNumberCountryCode} ${c.phoneNumber})`}</div>
				)),
			),
		},
		{
			label: "Company unique identifier",
			node: tryGetProp("", () => data.company!.uniqueCompanyIdentifier),
		},
		{
			label: "Created date",
			node: <DateDisplay date={tryGetProp("", () => data.subscription!.createdDate)} fontSize={14} />,
		},
		{
			label: "Customer key",
			node: tryGetProp("", () => data.subscription!.customerKey),
		},
		{
			label: "Customer description",
			node: tryGetProp("", () => data.subscription!.customerKeyDescription),
		},
		{
			label: "CLE type",
			node: cleType,
		},
		{
			label: "Subscription additional configuration",
			node: tryGetProp(<span></span>, () => {
				return (
					<Box display="flex" alignItems="center">
						{isTwilioCleEnabled && hasPowFeature(POW_FEATURES.paSubscriptions_ModifyAdditionalConfig) && (
							<EditAdditionalSubscriptionConfig
								done={forceUpdate}
								productId={productId}
								subscriptionId={tryGetProp("", () => data.subscription!.subscriptionId)}
								config={tryGetTwilioConfig()}
							/>
						)}
						{isAmazonConnectCleEnabled && hasPowFeature(POW_FEATURES.paSubscriptions_ModifyAdditionalConfig) && (
							<EditAmazonConnectAdditionalSubscriptionConfig
								done={forceUpdate}
								productId={productId}
								subscriptionId={tryGetProp("", () => data.subscription!.subscriptionId)}
								config={tryGetAmazonConnectConfig()}
							/>
						)}
						{!hasPowFeature(POW_FEATURES.paSubscriptions_ModifyAdditionalConfig) && (
							<ViewJsonDialog
								label="View configuration"
								title="Subscription Additional Configuration"
								json={tryJsonParse(data.subscription!.subscriptionAdditionalConfiguration)}
							/>
						)}
						{!isValidConfig && (
							<Chip label="Invalid format" className={classes.invalidBadge} variant="outlined" size="small" />
						)}
					</Box>
				);
			}),
		},
		{
			label: "Automatically renew subscription",
			node: tryGetProp("", () => boolToYesNo(data.subscription!.autoRenew)),
		},
		{
			label: "Deployment ring id",
			node: tryGetProp("", () => data.subscription!.deploymentRingId),
		},
	];

	return (
		<div>
			<GridValueDisplayList displayItems={detailsToDisplay}></GridValueDisplayList>
			{isTwilioCleEnabled && (
				<PowFeature feature={POW_FEATURES.paSubscriptions_Details_DownloadWorkers}>
					<WorkerListDownload
						productId={productId}
						subscriptionId={data.subscription!.subscriptionId}
						companyName={data.company!.companyName}
					></WorkerListDownload>
				</PowFeature>
			)}
		</div>
	);
};

/**
 * Page to display subscription details and allow configuration editing
 * @param props
 */
export const SubscriptionDetailsPage = (props: SubscriptionDetailsPageProps) => {
	const classes = useStyles();
	const { subscription, setSubscription } = React.useContext(store);

	const updateFunction = () => {
		setSubscription!(undefined);
	};
	return (
		<div className={classes.wrapper} data-testid="subscription-details-values-list">
			<Typography className={classes.title} variant="h4">
				Subscription Details
			</Typography>
			<Fetcher
				loadedData={() =>
					subscription && subscription.subscription!.subscriptionId === props.subscriptionId ? subscription : null
				}
				action={getSubscriptionDetails(props.productId, props.subscriptionId)}
			>
				{(response) => renderPage(response && response.data, classes, props.productId, updateFunction)}
			</Fetcher>
		</div>
	);
};
